import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { CommonModule } from '@angular/common';

/** Material */
import { MatExpansionModule } from '@angular/material/expansion';

/** 3rd Party */
import { TranslateModule } from '@ngx-translate/core';
import { NgScrollbarModule } from 'ngx-scrollbar';

/** Services */
import { CustomizerSettingsService } from 'src/app/core/services/customizer-settings.service';
import { ToggleService } from 'src/app/core/services/toggle.service';

/** Constants */
import { PermissionService } from 'src/app/core/services/permission.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { ROUTES } from 'src/app/shared/constants/app-routes';

import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Subject } from 'rxjs';
import {
  debounceTime,
  takeUntil,
  switchMap,
  filter,
  tap,
} from 'rxjs/operators';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    NgScrollbarModule,
    MatExpansionModule,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnDestroy {
  ROUTES = ROUTES;
  profile: any;
  panelOpenState = false;
  userProfile: any;
  userRoles: any;
  isToggled = false;
  isMobileView = false;
  sideBarItems: any[] = [];
  userType: string = '';
  isLoading: boolean = true;
  private destroy$ = new Subject<void>();
  private resizeListener: () => void;

  constructor(
    private toggleService: ToggleService,
    public themeService: CustomizerSettingsService,
    private authService: AuthService,
    public router: Router,
    public permissionService: PermissionService,
    private cdr: ChangeDetectorRef,
  ) {
    this.toggleService.isToggled$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isToggled) => {
        this.isToggled = isToggled;
      });

    this.authService.profile$
      .pipe(
        debounceTime(100),
        filter((user) => !!user),
        tap((user) => {
          this.profile = this.authService.getUserRoles();
          this.userProfile = user;
          this.userType = this.userProfile.attributes?.userType[0];
          this.userRoles = this.authService.getUserRoles();
          // console.log('User Roles in Sidebar:', this.userRoles);
        }),
        switchMap(() => this.permissionService.currentUser$),
        filter((currentUser) => !!currentUser),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.initializeSideBarItems();
      });

    // Check if we're on mobile view
    this.checkScreenSize();
    this.resizeListener = () => this.checkScreenSize();
    window.addEventListener('resize', this.resizeListener);
  }

  ngOnDestroy() {
    // Clean up subscriptions and event listener
    window.removeEventListener('resize', this.resizeListener);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeSideBarItems() {
    this.isLoading = true;
    this.sideBarItems = [
      {
        title: 'Accreditations',
        active: '/accreditations',
        permitted:
          this.userProfile.attributes?.userType[0] == 'Institution' ||
          this.userProfile.attributes?.userType[0] == '2' ||
          this.permissionService.hasModulePermission('ACC') ||
          this.permissionService.hasModulePermission('ADM') ||
          this.userRoles.includes('Admin'),
        displayFor: ['Staff', 'Institution', '0', '2'],
        icon: 'flaticon-learning',
        items: [
          {
            title: 'tvet.capitalize',
            routerLink: '/accreditations/tvet-accreditations',
            icon: 'flaticon-learning',
            permitted:
              this.permissionService.hasPermission('ACC-VLTASR') ||
              this.userProfile.attributes?.userType[0] == 'Institution' ||
              this.userProfile.attributes?.userType[0] == '2' ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'general_education.capitalize',
            routerLink: '/accreditations/general-education-accreditations',
            icon: 'flaticon-online-learning',
            permitted:
              this.permissionService.hasPermission('ACC-VLGEASR') ||
              this.userProfile.attributes?.userType[0] == 'Institution' ||
              this.userProfile.attributes?.userType[0] == '2' ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'Institutions',
            routerLink: '/accreditations/institutions',
            icon: 'flaticon-house',
            permitted:
              this.permissionService.hasPermission('ADM-VLINS') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'migrations.titlecase',
            routerLink: '/accreditations/migrations',
            icon: 'flaticon-cloud-computing',
            permitted: this.userRoles.includes('Admin'),
          },
          {
            title: 'settings.capitalize',
            routerLink: '/accreditations/settings',
            icon: 'flaticon-settings',
            permitted: this.userRoles.includes('Admin'),
          },
          {
            title: 'available_evaluators.capitalize',
            routerLink: '/accreditations/available-evaluators',
            icon: 'flaticon-user',
            permitted:
              this.userRoles.includes('Admin') ||
              this.permissionService.hasPermission('ACC-VAE'),
          },
        ],
      },
      {
        title: 'branches.titlecase',
        active: '/institutions',
        permitted:
          this.userProfile.attributes?.userType[0] == 'Institution' ||
          this.userProfile.attributes?.userType[0] == '2' ||
          this.userRoles.includes('Admin'),
        displayFor: ['Institution', '2'],
        icon: 'flaticon-home',
        items: [
          {
            title: 'tvet.capitalize',
            routerLink: '/institutions/tvet/branches',
            icon: 'flaticon-learning',
            permitted:
              this.userProfile.attributes?.userType[0] == 'Institution' ||
              this.userProfile.attributes?.userType[0] == '2',
          },
          {
            title: 'general_education.capitalize',
            routerLink: '/institutions/general-education/branches',
            icon: 'flaticon-learning',
            permitted:
              this.userProfile.attributes?.userType[0] == 'Institution' ||
              this.userProfile.attributes?.userType[0] == '2',
          },
        ],
      },
      {
        title: 'students.titlecase',
        active: '/institutions/students',
        permitted:
          this.userProfile.attributes?.userType[0] == 'Institution' ||
          this.userProfile.attributes?.userType[0] == '2',
        displayFor: ['Institution', '2'],
        icon: 'flaticon-people',
        items: [
          {
            title: 'students.titlecase',
            routerLink: '/institutions/students',
            icon: 'flaticon-people',
            permitted:
              this.userProfile.attributes?.userType[0] == 'Institution' ||
              this.userProfile.attributes?.userType[0] == '2',
          },
          {
            title: 'applications.titlecase',
            routerLink: '/institutions/students/applications',
            icon: 'flaticon-to-do-list',
            permitted:
              this.userProfile.attributes?.userType[0] == 'Institution' ||
              this.userProfile.attributes?.userType[0] == '2',
          },
          {
            title: 'results.titlecase',
            routerLink: '/institutions/students/results',
            icon: 'flaticon-award',
            permitted:
              this.userProfile.attributes?.userType[0] == 'Institution' ||
              this.userProfile.attributes?.userType[0] == '2',
          },
        ],
      },
      {
        title: 'Candidate Assessments',
        active: '/assessments',
        displayFor: ['Staff', 'Individual', '1', '0'],
        permitted:
          ((this.userProfile.attributes?.userType[0] == 'Individual' ||
            this.userProfile.attributes?.userType[0] == '1') &&
            this.userProfile.attributes?.individualType[0] == 0) ||
          this.permissionService.hasModulePermission('ASS') ||
          this.userRoles.includes('Admin'),
        icon: 'flaticon-check',
        items: [
          {
            title: 'my_profile.capitalize',
            routerLink: '/assessments/profile',
            icon: 'flaticon-users-group',
            permitted:
              (this.userProfile.attributes?.userType[0] == 'Individual' ||
                this.userProfile.attributes?.userType[0] == '1') &&
              this.userProfile.attributes?.individualType[0] == 0,
          },
          {
            title: 'applications.titlecase',
            routerLink: '/assessments/candidates',
            icon: 'flaticon-users-group',
            permitted:
              this.permissionService.hasPermission('ASS-VCANDS') ||
              this.userRoles.includes('Admin') ||
              ((this.userProfile.attributes?.userType[0] == 'Individual' ||
                this.userProfile.attributes?.userType[0] == '1') &&
                this.userProfile.attributes?.individualType[0] == 0),
          },
          {
            title: 'application_approvals.titlecase',
            routerLink: '/assessments/application-approvals',
            icon: 'flaticon-checkmark',
            permitted:
              this.permissionService.hasPermission('ASS-APASAPR') ||
              this.permissionService.hasPermission('ASS-RJCASAPR') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'schedule.titlecase',
            routerLink: '/assessments/schedule',
            icon: 'flaticon-to-do-list',
            permitted:
              this.permissionService.hasPermission('ASS-VSCHL') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'assessment_exam.titlecase',
            routerLink: '/assessments/welcome-exam',
            icon: 'flaticon-to-do-list',
            permitted:
              this.userProfile.attributes?.userType[0] == 'Individual' ||
              this.userProfile.attributes?.userType[0] == '1',
          },
          {
            title: 'assessment_results.titlecase',
            routerLink: '/assessments/assessment-result',
            icon: 'flaticon-people',
            permitted:
              this.permissionService.hasPermission('ASS-VCANDRSL') ||
              this.userProfile.attributes?.userType[0] == 'Individual' ||
              this.userProfile.attributes?.userType[0] == '1' ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'coc_summary.titlecase',
            routerLink: '/assessments/coc-summary',
            icon: 'flaticon-people',
            permitted:
              this.userRoles.includes('Admin') ||
              this.userRoles.includes('Branch Admin'),
          },

          {
            title: 'reassessment.titlecase',
            routerLink: '/assessments/reassessment',
            icon: 'flaticon-tools',
            permitted: this.userRoles.includes('Admin'),
          },
          {
            title: 'assessment_centers.titlecase',
            routerLink: '/assessments/centers',
            icon: 'flaticon-home',
            permitted:
              this.permissionService.hasPermission('ASS-VASSCNTRL') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'assessors.titlecase',
            routerLink: '/assessments/assessors',
            icon: 'flaticon-people',
            permitted:
              this.permissionService.hasPermission('ASS-VSSRL') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'supervisors.titlecase',
            routerLink: '/assessments/supervisors',
            icon: 'flaticon-people',
            permitted:
              this.permissionService.hasPermission('ASS-VSPRVL') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'candidate_tool_version.titlecase',
            routerLink: '/assessments/tool-version',
            icon: 'flaticon-tools',
            permitted:
              this.permissionService.hasPermission('ASS-VEXMVERL') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'reporting.titlecase',
            routerLink: '/assessments/dashboard',
            icon: 'flaticon-dashboard',
            permitted: this.userRoles.includes('Admin'),
          },
          {
            title: 'available_assessors.capitalize',
            routerLink: '/assessments/available-assessors',
            icon: 'flaticon-user',
            permitted: this.userRoles.includes('Admin'),
          },
          {
            title: 'available_supervisors.capitalize',
            routerLink: '/assessments/available-supervisors',
            icon: 'flaticon-user',
            permitted: this.userRoles.includes('Admin'),
          },
          {
            title: 'settings.titlecase',
            routerLink: '/assessments/settings',
            permitted: this.userRoles.includes('Admin'),
          },
        ],
      },
      {
        title: 'Teacher Assessments',
        active: '/academic-personnel',
        permitted:
          ((this.userProfile.attributes?.userType[0] == 'Individual' ||
            this.userProfile.attributes?.userType[0] == '1') &&
            this.userProfile.attributes?.individualType[0] == 1) ||
          ((this.userProfile.attributes?.userType[0] == 'Staff' ||
            this.userProfile.attributes?.userType[0] == '0') &&
            this.permissionService.hasModulePermission('ASS')) ||
          this.userRoles.includes('Branch Admin') ||
          this.userRoles.includes('Admin'),
        displayFor: ['Staff', 'Individual', '0', '1'],
        icon: 'flaticon-people',
        items: [
          {
            title: 'AP Report',
            routerLink: '/academic-personnel/report',
            icon: 'ri ri-file-list-line',
            permitted:
              this.userRoles.includes('Admin') ||
              this.userRoles.includes('Branch Admin'),
          },
          {
            title: 'Dashboard',
            routerLink: '/academic-personnel/dashboard',
            icon: 'ri ri-dashboard-line',
            permitted:
              this.userRoles.includes('Admin') ||
              this.userRoles.includes('Branch Admin'),
          },
          {
            title: 'batches.titlecase',
            routerLink: '/academic-personnel/batches',
            icon: 'ri ri-group-3-line',
            permitted:
              this.permissionService.hasPermission('ASS-VWBCH') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'blocked_exam_records.titlecase',
            routerLink: '/academic-personnel/blocked-exam-records',
            icon: 'ri ri-spam-2-line',
            permitted:
              this.permissionService.hasPermission('ASS-VBKAPEXM') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'license_applications.titlecase',
            routerLink: '/academic-personnel/license-applications',
            icon: 'ri ri-file-list-3-line',
            permitted:
              this.permissionService.hasPermission('ASS-VEXMVERL') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'exam_rounds.titlecase',
            routerLink: '/academic-personnel/exam-rounds',
            icon: 'ri ri-recycle-line',
            permitted:
              this.permissionService.hasPermission('ASS-VWRND') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'teacher_results.titlecase',
            routerLink: '/academic-personnel/teacher-results',
            icon: 'flaticon-people',
            permitted:
              this.permissionService.hasPermission('ASS-VEXMVERL') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'portfolio.titlecase',
            routerLink: '/academic-personnel/portfolio',
            icon: 'flaticon-people',
            permitted:
              this.permissionService.hasPermission('ASS-VEXMVERL') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'assessors.titlecase',
            routerLink: '/academic-personnel/assessors',
            icon: 'flaticon-people',
            permitted:
              this.permissionService.hasPermission('ASS-VWAPA') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'assessment_centers.titlecase',
            routerLink: '/academic-personnel/assessment-centers',
            icon: 'flaticon-home',
            permitted:
              this.permissionService.hasPermission('ASS-VWAPAC') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'tool_version_teacher.titlecase',
            routerLink: '/academic-personnel/tool-version-teacher',
            icon: 'ri ri-tools-line',
            permitted:
              this.permissionService.hasPermission('ASS-VAPEXMVERL') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'profile.titlecase',
            routerLink: '/profile/academic-personnel',
            icon: 'ri-user-3-line', // remix icon
            permitted:
              (this.userProfile.attributes?.userType[0] == 'Individual' ||
                this.userProfile.attributes?.userType[0] == '1') &&
              this.userProfile.attributes?.individualType[0] == 1,
          },
          {
            title: 'applications.titlecase',
            routerLink: '/academic-personnel/applications',
            icon: 'ri-archive-line', // remix icon
            permitted:
              (this.userProfile.attributes?.userType[0] == 'Individual' ||
                this.userProfile.attributes?.userType[0] == '1') &&
              this.userProfile.attributes?.individualType[0] == 1,
          },
          {
            title: 'exams.titlecase',
            routerLink: '/academic-personnel/academic-personnel-exam',
            icon: 'ri-file-list-2-line', // remix icon
            permitted:
              (this.userProfile.attributes?.userType[0] == 'Individual' ||
                this.userProfile.attributes?.userType[0] == '1') &&
              this.userProfile.attributes?.individualType[0] == 1,
          },
          {
            title: 'my_results.titlecase',
            routerLink: '/academic-personnel/teacher-own-results',
            icon: 'ri-medal-line', // remix icon
            permitted:
              (this.userProfile.attributes?.userType[0] == 'Individual' ||
                this.userProfile.attributes?.userType[0] == '1') &&
              this.userProfile.attributes?.individualType[0] == 1,
          },
        ],
      },
      {
        title: 'Inspection',
        active: '/inspection',
        permitted:
          this.permissionService.hasModulePermission('INS') ||
          this.userProfile.attributes?.userType[0] == 'Institution' ||
          this.userProfile.attributes?.userType[0] == '2' ||
          this.userRoles.includes('Admin'),
        displayFor: ['Staff', 'Institution', '0', '2'],
        icon: 'flaticon-visualization',
        items: [
          {
            title: 'set_schedule.titlecase',
            routerLink: '/inspection/schedules/view',
            icon: 'flaticon-verify',
            permitted:
              this.permissionService.hasPermission('INS-SII') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'scheduled_inspections.titlecase',
            routerLink: '/inspection/history',
            icon: 'flaticon-analytics',
            permitted:
              this.permissionService.hasPermission('INS-VIS') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'inspection_results.titlecase',
            routerLink: '/inspection/results',
            icon: 'flaticon-analytics',
            permitted:
              this.permissionService.hasPermission('INS-VTIR') ||
              this.permissionService.hasPermission('INS-VGEIR') ||
              this.userProfile.attributes?.userType[0] == 'Institution' ||
              this.userProfile.attributes?.userType[0] == '2' ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'reporting.titlecase',
            routerLink: '/inspection/dashboard',
            icon: 'flaticon-visualization',
            permitted:
              this.permissionService.hasPermission('INS-SII') ||
              this.userRoles.includes('Admin'),
          },
          // {
          //   title: 'Inspection Report',
          //   routerLink: '/inspection/report',
          //   icon: 'ri ri-file-list-line',
          //   permitted:
          //     this.permissionService.hasPermission('INS-VTD') ||
          //     this.userRoles.includes('Admin'),
          // },
          {
            title: 'available_inspectors.capitalize',
            routerLink: '/inspection/available-inspectors',
            icon: 'flaticon-user',
            permitted:
              this.permissionService.hasPermission('INS-SII') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'settings.titlecase',
            routerLink: '/inspection/settings',
            icon: 'flaticon-settings',
            permitted: this.userRoles.includes('Admin'),
          },
          {
            title: 'migrations.titlecase',
            routerLink: '/inspection/migrations/inspection-histories',
            icon: 'flaticon-cloud-computing',
            permitted: this.userRoles.includes('Admin'),
          },
        ],
      },
      {
        title: 'Document Authentication',
        active: '',
        displayFor: ['Staff', '0'],
        permitted:
          this.userRoles.includes('Admin') ||
          this.permissionService.hasModulePermission('DOCA'),
        icon: 'flaticon-to-do-list',
        items: [
          {
            title: 'requests.capitalize',
            routerLink: '/document-authentication/requests',
            icon: 'flaticon-file',
            permitted:
              this.permissionService.hasPermission('DOCA-FDOCAR') ||
              this.permissionService.hasPermission('DOCA-ADOCAR') ||
              this.userRoles.includes('Admin'),
          },
          {
            title: 'student_data.capitalize',
            routerLink: '/document-authentication/student-data',
            icon: 'flaticon-user',
            permitted:
              this.userRoles.includes('Admin') ||
              this.permissionService.hasPermission('DOCA-VSD'),
          },
          {
            title: 'settings.capitalize',
            routerLink: '/document-authentication/settings',
            icon: 'flaticon-settings',
            permitted: this.userRoles.includes('Admin'),
          },
        ],
      },
      {
        title: 'User & Role Management',
        active: '',
        displayFor: ['Staff', '0'],
        permitted:
          this.userRoles.includes('Admin') ||
          this.userRoles.includes('Branch Admin'),
        icon: 'flaticon-people',
        items: [
          {
            title: 'users.capitalize',
            routerLink: '/users',
            icon: 'flaticon-user',
            permitted:
              this.userRoles.includes('Admin') ||
              this.userRoles.includes('Branch Admin'),
          },
          {
            title: 'roles.capitalize',
            routerLink: '/roles',
            icon: 'flaticon-shield',
            permitted: this.userRoles.includes('Admin'),
          },
          {
            title: 'identity.capitalize',
            routerLink: '/users/idp',
            icon: 'flaticon-user',
            permitted: this.userRoles.includes('Admin'),
          },
        ],
      },
      {
        title: 'Configurations',
        active: '/configurations',
        permitted: this.userRoles.includes('Admin'),
        displayFor: ['Staff', '0'],
        icon: 'flaticon-settings',
        items: [
          {
            title: 'portal.capitalize',
            routerLink: '/configurations/portal',
            icon: 'flaticon-worldwide',
            permitted: this.userRoles.includes('Admin'),
          },
          {
            title: 'setting.capitalize',
            routerLink: '/configurations/settings',
            icon: 'flaticon-settings',
            permitted: this.userRoles.includes('Admin'),
          },
          {
            title: 'feedbacks.capitalize',
            routerLink: '/feedbacks',
            icon: 'flaticon-comment',
            permitted: this.userRoles.includes('Admin'),
          },
          {
            title: 'payment.capitalize',
            routerLink: '/configurations/payment',
            icon: 'ri-secure-payment-line',
            permitted: this.userRoles.includes('Admin'),
          },
          {
            title: 'audit_logs.capitalize',
            routerLink: '/audit-logs',
            icon: 'ri-file-list-3-line',
            permitted: this.userRoles.includes('Admin'),
          },
        ],
      },
      {
        title: 'Migrations',
        active: '/migrations',
        permitted: this.userRoles.includes('Admin'),
        displayFor: ['Staff', '0'],
        icon: 'flaticon-cloud-computing',
        items: [
          {
            title: 'data_migration.titlecase',
            routerLink: '/migrations/import-data',
            icon: 'flaticon-cloud-computing',
            permitted: this.userRoles.includes('Admin'),
          },
          {
            title: 'checklist.capitalize',
            routerLink: '/checklist',
            icon: 'flaticon-to-do-list',
            permitted: this.userRoles.includes('Admin'),
          },
          {
            title: 'user_complaint.capitalize',
            routerLink: '/user-complaint',
            icon: 'flaticon-user-1',
            permitted: this.userRoles.includes('Admin'),
          },
        ],
      },
      {
        title: 'Audit Logs',
        active: '/',
        permitted: this.userRoles.includes('Admin'),
        displayFor: ['Staff', '0'],
        icon: 'flaticon-list-1',
        items: [
          {
            title: 'audit_logs.capitalize',
            routerLink: '/audit-logs',
            icon: 'ri-file-list-3-line',
            permitted: this.userRoles.includes('Admin'),
          },
        ],
      },
    ];

    this.isLoading = false;

    this.cdr.detectChanges();
  }

  private checkScreenSize() {
    this.isMobileView = window.innerWidth < 992;
  }

  closeMenuOnMobile() {
    if (this.isMobileView && this.isToggled) {
      this.toggleService.toggle();
    }
  }

  toggle() {
    this.toggleService.toggle();
  }

  toggleSidebarTheme() {
    this.themeService.toggleSidebarTheme();
  }

  toggleHideSidebarTheme() {
    this.themeService.toggleHideSidebarTheme();
  }
}
